import uuid from "uuid-random";
import { cloneDeep } from "lodash";
import { Classroom, Day, Instructor, Session } from "@models/ISchema";
import { compareSessions } from "./compareSelected";
import { blocksFormat, intervalsFormat } from "../utils/sessionFormat";
import { LinkPageType, Week } from "../context/linkData.reducer";
import {
  Assignment,
  EditedSession,
  EditedSessions,
  ForkWeekLockPredicate,
  FormPageType,
} from "../context/formData.reducer";
import { getOriginalSessionsById, OriginalSession } from "./getOriginalSessionsById";
import { sessionsHaveTheSameResources } from "./sessionsHaveTheSameResources";

export const initEditedSessions = (
  selectedSessions: (Session | EditedSession)[],
  link: LinkPageType,
): {
  editedSessions: EditedSessions;
  assignmentSame: Assignment;
} => {
  let editedSession: {
    editedSessions: EditedSessions;
    assignmentSame: Assignment;
  };

  if (selectedSessions.length === 1) {
    editedSession = {
      editedSessions: selectedSessions[0],
      assignmentSame: {
        blocks: true,
        day: true,
        instructors: true,
        classrooms: true,
        intervals: true,
      },
    };
  } else {
    const compare = compareSessions(selectedSessions, link?.weeks ?? []);

    editedSession = {
      editedSessions: compare.editedSessions,
      assignmentSame: compare.assignmentSame,
    };
  }
  return editedSession;
};

/**
 * Given a FormPageType state, return a `sessionToCreate` for each selected
 * day, where each new `sessionToCreate` has one of the selected days selected.
 *
 * @param formState FormPageType
 * @return EditedSession[]
 */
export const sessionsToCreateBySelectedDays = (formState: FormPageType): EditedSession[] => {
  const editedSessions = formState?.editedSessions;
  const selectedSection = formState?.selectedSection;

  if (!editedSessions || !editedSessions?.blocks) return [];

  const { days: selectedDays, startTime, endTime } = editedSessions?.blocks;

  const emptyBlock = (day: Day) => ({
    day,
    days: [day],
    startTime,
    endTime,
    blocks: undefined,
    selected: "hours" as "hours" | "blocks",
  });

  return (selectedDays ?? []).map((day: Day) => ({
    id: uuid(),
    blocks: emptyBlock(day),
    instructors: (editedSessions?.instructors ?? []) as Instructor[],
    classrooms: (editedSessions?.classrooms ?? []) as Classroom[],
    intervals: (editedSessions?.intervals ?? []) as Week[],
    session: null as Session,
    isNew: true,
    isCloned: false,
    section: cloneDeep(selectedSection),
  }));
};

/**
 * Given a FormPageType state, returns the sessions to add to the editSessions
 * view.
 *
 * @param formState FormPageType
 * @return EditedSession[]
 */
export const newSessionsToCreateByFormState = (
  formState: FormPageType,
  isNewOrCloned: "cloned" | "new",
): EditedSession[] => {
  const uniqueSessionToSourceFrom = (state: FormPageType): boolean => {
    const noSavedSessions = !state?.savedSessions || state.savedSessions.length === 0;
    const hasOneSelectionSession = !!state?.selectedSessions && state.selectedSessions.length === 1;
    return noSavedSessions && hasOneSelectionSession;
  };

  const editedSessions = formState?.editedSessions;

  if (uniqueSessionToSourceFrom(formState)) {
    return [
      {
        id: uuid(),
        blocks: formState?.editedSessions?.blocks,
        instructors: formState?.editedSessions?.instructors,
        classrooms: formState?.editedSessions?.classrooms,
        intervals: formState?.editedSessions?.intervals,
        session: null as Session,
        isNew: isNewOrCloned === "new",
        isCloned: isNewOrCloned === "cloned",
        section: formState?.selectedSessions?.[0]?.section,
      },
    ];
  }

  const emptyBlock = {
    day: undefined,
    days: [],
    startTime: undefined,
    endTime: undefined,
    blocks: undefined,
    selected: undefined as "hours" | "blocks",
  };

  return formState?.selectedSessions.map(session => {
    const clonnedBlocks = cloneDeep((session as EditedSession)?.blocks || emptyBlock);
    const blocks =
      isNewOrCloned === "cloned"
        ? clonnedBlocks
        : editedSessions?.blocks
          ? cloneDeep(editedSessions?.blocks)
          : cloneDeep(emptyBlock);

    const instructors =
      isNewOrCloned === "cloned"
        ? cloneDeep((session as EditedSession)?.instructors || [])
        : editedSessions?.instructors;
    const classrooms =
      isNewOrCloned === "cloned"
        ? cloneDeep((session as EditedSession)?.classrooms || [])
        : editedSessions?.classrooms;
    const intervals =
      isNewOrCloned === "cloned"
        ? cloneDeep((session as EditedSession)?.intervals || [])
        : editedSessions?.intervals;

    return {
      id: uuid(),
      blocks,
      instructors,
      classrooms,
      intervals,
      session: null as Session,
      isNew: isNewOrCloned === "new",
      isCloned: isNewOrCloned === "cloned",
      section: session?.section,
    };
  });
};

/**
 * Return true if the given session have at least one week checked and it's
 * blocks defined, false otherwise.
 *
 * @param session EditedSession
 * @return boolean
 */
export const sessionIsSavable = (session: EditedSession): boolean => {
  const hasCheckedWeeks = (session?.intervals ?? []).some(
    week => Object.prototype.hasOwnProperty.call(week, "checked") && week.checked === true,
  );
  const hasBlocksDefined = Boolean(session?.blocks?.selected);
  return hasCheckedWeeks && hasBlocksDefined;
};

/**
 * Return true if all the given sessions have at least one week checked and
 * it's blocks defined.
 *
 * @param session EditedSession[]
 * @return boolean
 */
export const allSessionsCanBeSaved = (sessions: EditedSession[]): boolean =>
  sessions.every(sessionIsSavable);

/**
 * Filter the given sessions by the ones that have at least one week checked
 * and their blocks defined.
 *
 * @param session EditedSession[]
 * @return EditedSession[]
 */
export const savableSessionsToCreate = (sessions: EditedSession[]): EditedSession[] =>
  sessions.filter(sessionIsSavable);

export const getNewEditedSessions = (
  savedSessions: EditedSession[],
  selected: (Session | EditedSession)[],
  link: LinkPageType,
): { editedSessions?: EditedSessions; assignmentSame?: Assignment } => {
  if (!savedSessions.length) return initEditedSessions(selected, link);

  const savedSessionsById = savedSessions.reduce(
    (acc: Record<string, EditedSession>, curr) => ({ ...acc, [curr.id]: curr }),
    {},
  );

  // Si la sesión ya había sido guardada compara la sesión guardada
  const selectedSessions = selected.map(
    selectedSession => savedSessionsById[selectedSession.id] ?? selectedSession,
  );
  const { editedSessions, assignmentSame } = compareSessions(selectedSessions, link?.weeks ?? []);
  return selectedSessions.length ? { editedSessions, assignmentSame } : {};
};

/**
 * Given a `checked`, the sessions of the selected section, the form context
 * and the sectionId, return an array with the new context's selected sessions.
 *
 * @param checked boolean
 * @param sessionsOfSection Session[]
 * @param form FormPageType
 * @return (Session | EditedSession)[]
 */
export const getNewSelectedSessionsBySection = (
  checked: boolean,
  sessionsOfSection: Session[],
  form: FormPageType,
  sectionId: string,
) => {
  const belongsToSelectedSection = (session: any) => {
    const section = session?.section || {};
    const id = section?.id || "";
    return id === sectionId;
  };

  const selectedSessionsOfOtherSections = (form?.selectedSessions ?? []).filter(
    session => !belongsToSelectedSection(session),
  );

  if (!checked) {
    return selectedSessionsOfOtherSections;
  } else {
    const sessionsToCreateInSection = (form?.sessionsToCreate ?? []).filter(
      belongsToSelectedSection,
    );

    /**
     * Remove duplicates from the merged sessions array
     */
    const uniqueSessions = [
      ...sessionsToCreateInSection,
      ...sessionsOfSection,
      ...selectedSessionsOfOtherSections,
    ].reduce((acc, session) => {
      acc[session.id] = session;
      return acc;
    }, {});

    return Object.values(uniqueSessions);
  }
};

/**
 * Given a single session and an array of sessions, return a copy of the given
 * array of sessions with the single session `added` if it wasn't there or
 * `removed` if it was already there.
 *
 * @param selectedSession Session | EditedSession
 * @param currentSelectedSessions (Session | EditedSession)[]
 * @return (Session | EditedSession)[]
 */
export const getNewSelectedSessions = (
  selectedSession: Session | EditedSession,
  currentSelectedSessions: EditedSession[],
) => {
  const selectedSessionsById = (currentSelectedSessions ?? []).reduce((acc, session) => {
    acc[session.id] = session;
    return acc;
  }, {});

  if (selectedSessionsById[selectedSession?.id]) {
    return [...(currentSelectedSessions ?? [])].filter(
      session => session.id !== selectedSession?.id,
    );
  }

  return [...(currentSelectedSessions ?? []), selectedSession];
};

/**
 * Returns an object with the following structure:
 * {
 *   assignmentSame: {
 *     blocks: boolean,
 *     instructors: boolean,
 *     classrooms: boolean,
 *     intervals: boolean,
 *   },
 *   cloneSelected: [],
 *   editedSessions: {
 *     blocks?: ?,
 *     instructors?: ?[],
 *     classrooms?: ?[],
 *     intervals: ?[],
 *   }
 * }
 *
 * ## assignmentSame
 * An object with a boolean for each editable resource. By each resource True
 * if the selected sessions have the same assignment between the resource, false
 * otherwise.
 *
 * ## cloneSelected
 * Array with a clone of the selected sessions.
 *
 * ## editedSessions
 * An object with the common resources between the selected sessions.
 *
 * @param selectedSession Sessio | EditedSession
 * @param form FormPageType
 * @param link LinkPageType
 */
export const validateSession = (
  selectedSession: Session | EditedSession,
  form: FormPageType,
  link: LinkPageType,
) => {
  const savedSessionsById = (form?.savedSessions ?? []).reduce((acc, session) => {
    acc[session.id] = session;
    return acc;
  }, {});

  const sessionsToCreateById = (form?.sessionsToCreate ?? []).reduce((acc, session) => {
    acc[session.id] = session;
    return acc;
  }, {});

  const currentSelectedSessions = (form?.selectedSessions ?? [])?.map(session => {
    const sessionToCreate = sessionsToCreateById[session?.id];
    const savedSession = savedSessionsById[session?.id];

    return sessionToCreate?.id ? sessionToCreate : savedSession?.id ? savedSession : session;
  });

  const newSelectedSessions = getNewSelectedSessions(selectedSession, currentSelectedSessions);

  const { editedSessions, assignmentSame } = getNewEditedSessions(
    form?.savedSessions ?? [],
    newSelectedSessions,
    link,
  );
  return { editedSessions, assignmentSame, cloneSelected: newSelectedSessions };
};

export const validateSavedSession = (
  form: FormPageType,
  link: LinkPageType,
  savedSessions: EditedSession[] = null,
) => {
  return getNewEditedSessions(
    savedSessions ? [...savedSessions] : [],
    form.selectedSessions ? [...form.selectedSessions] : [],
    link,
  );
};

const modifySession =
  (
    state: FormPageType,
    link: LinkPageType,
    savedSessionsById: { [key: string]: Session | EditedSession },
    sessionsToCreateById: { [key: string]: Session | EditedSession },
  ) =>
  (session: Session | EditedSession) => {
    const { assignmentEdited, editedSessions } = state;

    const sessionBlocks = (savedSession: EditedSession) => {
      return assignmentEdited.blocks
        ? editedSessions?.blocks
        : savedSession
          ? savedSession?.blocks
          : blocksFormat(session);
    };
    const sessionDay = (savedSession: EditedSession) => {
      return assignmentEdited?.day
        ? editedSessions?.blocks?.day
        : !!savedSession?.blocks?.day
          ? savedSession?.blocks?.day
          : ((session as Session)?.assignment?.blockRange?.start?.day ??
            (session as EditedSession)?.blocks?.day);
    };
    const sessionInstructors = (savedSession: EditedSession) => {
      return assignmentEdited?.instructors
        ? editedSessions?.instructors
        : savedSession
          ? savedSession?.instructors
          : session?.instructors;
    };
    const sessionClassrooms = (savedSession: EditedSession) => {
      return assignmentEdited.classrooms
        ? editedSessions?.classrooms
        : savedSession
          ? savedSession?.classrooms
          : session?.classrooms;
    };
    const sessionIntervals = (savedSession: EditedSession) => {
      return assignmentEdited?.intervals
        ? editedSessions?.intervals
        : savedSession
          ? savedSession?.intervals
          : intervalsFormat(session, link?.weeks ?? []);
    };

    const savedSession = savedSessionsById[session.id] ?? sessionsToCreateById[session.id];
    const newBlocks = sessionBlocks(savedSession);

    return {
      ...savedSession,
      id: session?.id,
      blocks: {
        ...newBlocks,
        day: sessionDay(savedSession),
        days: undefined,
      },
      instructors: sessionInstructors(savedSession),
      classrooms: sessionClassrooms(savedSession),
      intervals: sessionIntervals(savedSession),
      section: savedSession?.section ?? session?.section,
      session: session as Session,
    };
  };

export const handleSavedSessions = (state: FormPageType, link: LinkPageType) => {
  // define utility functions
  const sessionsById = (
    table: { [key: string]: Session | EditedSession },
    session: Session | EditedSession,
  ) => ({ ...table, [session.id]: session });
  const isSavedSession = (session: Session | EditedSession) =>
    !session?.id?.includes("-") && !(session as EditedSession)?.isNew;

  const savedSessionsById = (state?.savedSessions ?? [])
    .filter(isSavedSession)
    .reduce(sessionsById, {});
  const sessionsToCreateById = (state?.sessionsToCreate ?? []).reduce(sessionsById, {});

  // define the modified and unmodified sessions
  const modifiedSessions = (state?.selectedSessions ?? [])
    .map(modifySession(state, link, savedSessionsById, sessionsToCreateById))
    .filter((session: EditedSession) => {
      if (session.id in savedSessionsById) {
        return !(
          sessionsHaveTheSameResources(session, session?.session) &&
          sessionsHaveTheSameResources(session, savedSessionsById[session.id])
        );
      } else {
        return (
          !sessionsHaveTheSameResources(session, session?.session) ||
          session?.isNew ||
          session?.isCloned
        );
      }
    });

  const modifiedSessionsById = modifiedSessions.reduce(sessionsById, {});
  const unmodifiedSessions = (state?.savedSessions ?? []).filter(
    session => !(session.id in modifiedSessionsById),
  );

  // merge the modified and unmodified sessions in one array. Then, define the
  // `editedSessions` and `assignmentSame` objects for the context
  const newSavedSessions = [...unmodifiedSessions, ...modifiedSessions];
  const saveValidation = validateSavedSession(state, link, newSavedSessions);

  const clonedSessionsById = newSavedSessions.reduce(sessionsById, {});

  // since `naturalSessions` is used for the context's `savedSession`, we have
  // to remove the sessions that aren't modified in relation to what is saved on the DB.
  const naturalSessions = newSavedSessions
    .filter(isSavedSession)
    .filter(session => !sessionsHaveTheSameResources(session, session?.session));

  const sessionsToCreate = (state?.sessionsToCreate ?? []).map(sessionToCreate => {
    return sessionToCreate?.id in clonedSessionsById
      ? clonedSessionsById[sessionToCreate?.id]
      : sessionToCreate;
  });

  // define object with the ids of the session to create as keys
  const savedSessionsToCreateIds = newSavedSessions
    .filter(isSavedSession)
    .reduce((acc, s) => ({ ...acc, [s?.id]: true }), {});

  return {
    saveValidation,
    naturalSessions,
    sessionsToCreate,
    savedSessionsToCreateIds,
  };
};

/**
 * Split every session that mixes blocked and editable weeks in two: the original
 * session keeps, with its original assignment, the weeks that are blocked or
 * that were unmarked during the edition, and a new cloned session takes the
 * weeks still marked with the edited assignment.
 *
 * Each session is forked against its own intervals and its own original
 * assignment. The shared `editedSessions` form state only describes the session
 * currently selected, so using it as the source of weeks made N individual
 * editions collapse into a single fork (CHS-2818).
 *
 * Time complexity: O(n * w)
 * Where `n` is the number of saved sessions and `w` the number of weeks of the term.
 *
 * @param formState FormPageType
 * @param link LinkPageType
 * @param sessionsToFork EditedSession[]
 * @param isLocked (week: Week, session: EditedSession) => boolean
 * @return { newSessions, savedSessions, unmarkedWeekIdsBySessionId }
 */
export const forkSelectedSessions = (
  formState: FormPageType,
  link: LinkPageType,
  sessionsToFork: EditedSession[],
  isLocked: ForkWeekLockPredicate,
): {
  newSessions: EditedSession[];
  savedSessions: EditedSession[];
  unmarkedWeekIdsBySessionId?: { [sessionId: string]: { [weekId: string | number]: Week } };
} => {
  const originalSessionsById = getOriginalSessionsById(link?.sections ?? []);
  const shouldForkById = (sessionsToFork ?? []).reduce<{ [sessionId: string]: boolean }>(
    (acc, session) => ({ ...acc, [session?.id]: true }),
    {},
  );

  const newSessions: EditedSession[] = [];
  const savedSessions: EditedSession[] = [];

  // The weeks the ORIGINAL session keeps: the ones it already had assigned that
  // are either locked or were unmarked during the edition. Unmarking a week
  // while forking means "do not apply this change to that week", not "unassign
  // that class" — the class still has to run, and it runs with the original
  // assignment. Everything else the session was holding moves to the fork, so
  // the split is driven by its own assignment instead of by the form state.
  //
  // `isLocked` comes from the caller because each fork surface locks weeks for
  // different reasons: the validate-time modal only cares about the blocking
  // threshold, while the manual card also locks weeks outside the term or that
  // the user cannot unassign. Both end up honouring the same split rule.
  //
  // We MUST read the assignment from `link.sections` rather than from
  // `session.session`: after a re-select from a saved card, the selectedSession
  // is the EditedSession, not the raw session, so `session.session` ends up
  // holding the previous edition instead of the original.
  const weeksKeptByOriginOf = (
    original: OriginalSession,
    intervals: Week[],
    session: EditedSession,
  ): { [weekId: string]: boolean } => {
    const weekById = (intervals ?? []).reduce<{ [weekId: string]: Week }>(
      (acc, week) => ({ ...acc, [week?.id]: week }),
      {},
    );

    return (original?.assignment?.intervals ?? []).reduce<{ [weekId: string]: boolean }>(
      (acc, interval) => {
        const week = weekById[interval?.id];
        if (!week) return acc;

        const wasUnmarked = !week?.checked;

        return isLocked?.(week, session) || wasUnmarked ? { ...acc, [interval?.id]: true } : acc;
      },
      {},
    );
  };

  // If the weeks left on the original match its backend assignment there is no
  // real change to persist. Skipping the push keeps the session out of the
  // update changeset and out of the validation stage, which is exactly what has
  // to happen when every one of its weeks is blocked.
  const trimmedIntervalsMatchOriginalAssignment = (
    trimmedIntervals: Week[],
    original: OriginalSession,
  ): boolean => {
    const trimmedCheckedIds = (trimmedIntervals ?? [])
      .filter((week: Week) => !!week?.checked)
      .map((week: Week) => week.id)
      .sort();
    const originalAssignedIds = (original?.assignment?.intervals ?? [])
      .map(interval => interval?.id)
      .sort();

    if (trimmedCheckedIds.length !== originalAssignedIds.length) return false;
    return trimmedCheckedIds.every((id, index) => id === originalAssignedIds[index]);
  };

  (formState?.savedSessions ?? []).forEach(session => {
    const original = originalSessionsById[session?.id];

    // Sessions outside this fork are carried over untouched: rebuilding them
    // from their original would silently drop the edition made on them.
    if (!shouldForkById[session?.id] || !original) {
      savedSessions.push(session);
      return;
    }

    const intervals = session?.intervals ?? [];
    const keptWeekIds = weeksKeptByOriginOf(original, intervals, session);

    const originalIntervals = intervals.map(week => ({
      ...week,
      checked: !!keptWeekIds[week?.id],
    }));
    const forkIntervals = intervals.map(week => ({
      ...week,
      checked: !!week?.checked && !keptWeekIds[week?.id],
    }));
    const forkedWeeks = forkIntervals.filter(week => week?.checked);

    newSessions.push({
      ...original,
      ...session,
      session: null,
      id: uuid(),
      isCloned: true,
      intervals: forkIntervals,
      assignment: {
        intervals: forkedWeeks,
      },
      forkedWeeksByOriginSessions: {
        [session?.id]: forkedWeeks.map(week => week?.id),
      },
    } as EditedSession);

    if (!trimmedIntervalsMatchOriginalAssignment(originalIntervals, original)) {
      // `session` has to hold the ADAPTED original, the same baseline
      // `modifySession` stores for every other edited session. The raw session
      // that `sessionAdapter` leaves there carries its weeks under
      // `assignment.intervals` and has no `intervals`, and `SavedSectionsList`
      // renders this field, so leaving it raw makes the card look weekless.
      savedSessions.push({
        ...original,
        intervals: originalIntervals,
        session: original,
      } as EditedSession);
    }
  });

  return {
    newSessions,
    savedSessions,
    unmarkedWeekIdsBySessionId: {},
  };
};
